import { Tree } from 'antd';
import { EventDataNode } from 'antd/lib/tree';
import cx from 'classnames';
import React from 'react';
import { connect } from 'react-redux';
import { ThunkDispatch } from 'redux-thunk';
import { HeaderBar } from '../../components/common';
import { ControllerModal } from '../../components/devicecontrol/DigiTrac/ControllerModal/ControllerModal';
import { DigiTrac, VelocityConfiguration } from '../../components/devicecontrol/Index';
import { EventTable } from '../../components/event';
import { getDefaultTablePaginationConfig } from '../../Helper';
import { User } from '../../model/AccountModel';
import { PaginationSetting, SortDirections } from '../../model/CommonModel';
import { ControllerContact, CurrentDeviceControlObj, DeviceObjectType, DeviceType, ObjectTypes, TimeZoneType } from '../../model/DeviceAdminModel';
import { StoreEvent } from '../../store';
import { setAllItemsPagination, setCurrentDevice } from '../../store/common/actions';
import {
	setCommandsSetsBy,
	setControllerContacts,
	setControllerContactsByData,
	setCredentialTemplatesBy,
	setDefaultTablePaginationSetting,
	setDigiTracFilterMode,
	setDoorGroupsBy,
	setGlobalIOGroupsBy,
	setGrandTimeZonesBy,
	setHolidaysBy,
	setMasterTimeZonesBy,
	setOperatorsBy,
	setReaderControlGroupsBy,
	setStandardTimeZonesBy,
} from '../../store/deviceControl/actions';
import { RootReducer } from '../../store/rootReducer';
import styles from './deviceControl.module.scss';

const user: User = getUser();
const { isAdmin, showEventsOnDeviceAdmPage } = user;

// Internal state for the component
interface State {
	expandedKeys: string[];
	currentDeviceObject: DeviceObjectType;
	currentDevice: DeviceType;
	propKey: number;
	selectedKeys: React.Key[];
}

// Props those being mapped from Store
interface StateProps {
	currentDevice: CurrentDeviceControlObj;
	isEditMode: boolean;
	currentTimeZone: TimeZoneType;
	itemSearchedPagination: string;
	tablePaginationSetting: PaginationSetting;
}

// Dispatchable methods (invoke our store's actions)
interface DispatchProps {
	onChangeDevice: (deviceType: CurrentDeviceControlObj) => void;
	setCommandsSetsBy: (paginationSetting?: PaginationSetting) => void;
	setContactsControllers: (type: DeviceObjectType, paginationSetting?: PaginationSetting) => void;
	setCredentialTemplatesBy: (paginationSetting?: PaginationSetting) => void;
	setDoorGroupsBy: (paginationSetting?: PaginationSetting) => void;
	setReaderControlGroupBy: (paginationSetting?: PaginationSetting) => void;
	setStandardTimeZonesBy: (paginationSetting: PaginationSetting) => void;
	setMasterTimeZonesBy: (paginationSetting: PaginationSetting) => void;
	setGrandMasterTimeZonesBy: (paginationSetting: PaginationSetting) => void;
	setHolidaysBy: (paginationSetting?: PaginationSetting) => void;
	setOperatorsBy: (paginationSetting?: PaginationSetting) => void;
	setControllerContactsByData: (type: DeviceObjectType, data: ControllerContact[]) => void;
	setGlobalIOGroupsBy: (paginationSetting?: PaginationSetting) => void;
	setAllItemsPagination: (deviceObjectType: DeviceObjectType, objectType?: ObjectTypes) => void;
	setDefaultTablePaginationSetting: () => void;
	setDigitracFilterMode: (isFilterMode: boolean) => void;
}

// Normal properties for the component
interface OwnProps {}

// combine them together
type Props = StateProps & DispatchProps & OwnProps;

export const getTitleComponent = (title: string, id: string): React.ReactNode => (
	<label title={title} id={id}>
		{title}
	</label>
);

const treeData = [
	{
		title: getTitleComponent(_('VelocityConfiguration'), 'velocityConfiguration'),
		key: '0-0',
		deviceType: DeviceType.Velocity,
		children: [
			{
				title: getTitleComponent(_('CommandSets'), 'velocityConfigurationCommandSets'),
				key: '0-0-0',
				deviceObjectType: DeviceObjectType.CommandSets,
			},
			{
				title: getTitleComponent(_('CredentialTemplates'), 'velocityConfigurationCredentialTemplates'),
				key: '0-0-1',
				deviceObjectType: DeviceObjectType.CredentialTemplates,
			},
			{
				title: getTitleComponent(_('DoorGroups'), 'velocityConfigurationDoorGroup'),
				key: '0-0-2',
				deviceObjectType: DeviceObjectType.DoorGroup,
			},
			{
				title: getTitleComponent(_('NetworkGlobalIOConfiguration'), 'velocityConfigurationNetworkGlobalIO'),
				key: '0-0-3',
				deviceObjectType: DeviceObjectType.GlobalIOGroup,
			},
			{
				title: getTitleComponent(_('ReaderControlGroups'), 'velocityConfigurationReaderControlGroup'),
				key: '0-0-4',
				deviceObjectType: DeviceObjectType.ReaderControlGroup,
			},
			{
				title: getTitleComponent(_('Holidays'), 'velocityConfigurationHoliday'),
				key: '0-0-5',
				deviceObjectType: DeviceObjectType.Holidays,
			},
			{
				title: getTitleComponent(_('TimeZones'), 'velocityConfigurationTimeZones'),
				key: '0-0-6',
				deviceObjectType: DeviceObjectType.TimeZones,
			},
			...(isAdmin
				? [{ title: getTitleComponent(_('Operators'), 'velocityConfigurationOperators'), key: '0-0-7', deviceObjectType: DeviceObjectType.Operators }]
				: []),
		],
	},
	{
		title: getTitleComponent(_('DigiTracConfiguration'), 'digiTracConfiguration'),
		key: '0-1',
		deviceType: DeviceType.DigiTrac,
		children: [
			{
				title: getTitleComponent(_('Ports'), 'digiTracConfigurationPort'),
				key: '0-1-0',
				deviceObjectType: DeviceObjectType.Port,
			},
			{
				title: getTitleComponent(_('Xboxes'), 'digiTracConfigurationXbox'),
				key: '0-1-1',
				deviceObjectType: DeviceObjectType.Xbox,
			},
			{
				title: getTitleComponent(_('Controllers'), 'digiTracConfigurationControllers'),
				key: '0-1-2',
				deviceObjectType: DeviceObjectType.Controller,
			},
			{
				title: getTitleComponent(_('Doors'), 'digiTracConfigurationDoors'),
				key: '0-1-3',
				deviceObjectType: DeviceObjectType.Door,
			},
			{
				title: getTitleComponent(_('ExpansionInputs'), 'digiTracConfigurationExpansionInputs'),
				key: '0-1-4',
				deviceObjectType: DeviceObjectType.ExpansionInputs,
			},
			{
				title: getTitleComponent(_('ExpansionRelays'), 'digiTracConfigurationExpansionRelays'),
				key: '0-1-5',
				deviceObjectType: DeviceObjectType.ExpansionRelays,
			},
			{
				title: getTitleComponent(_('Readers'), 'digiTracConfigurationReaders'),
				key: '0-1-6',
				deviceObjectType: DeviceObjectType.Reader,
			},
			{
				title: getTitleComponent(_('Inputs'), 'digiTracConfigurationInputs'),
				key: '0-1-7',
				deviceObjectType: DeviceObjectType.Input,
			},
			{
				title: getTitleComponent(_('Relays'), 'digiTracConfigurationRelays'),
				key: '0-1-8',
				deviceObjectType: DeviceObjectType.Relay,
			},
		],
	},
];

const paginationSetting: PaginationSetting = {
	PageNumber: 1,
	PageSize: getDefaultTablePaginationConfig()?.defaultPageSize,
	SortDirection: SortDirections.Ascend,
	SortField: '',
	SearchedValue: '',
};

class Page extends React.Component<Props, State> {
	constructor(prop: Props) {
		super(prop);
		this.state = {
			expandedKeys: ['0-1', '0-0-0'],
			currentDeviceObject: DeviceObjectType.CommandSets,
			currentDevice: DeviceType.Velocity,
			propKey: 1,
			selectedKeys: ['0-0-0'],
		};
	}

	private handleExpand = (selectedKeys, { node }) => {
		const { onChangeDevice } = this.props;
		// find nodes for current selection, only one child can be selected and always one must be selected
		const findNodeParent = selectedKeys.filter(x => x.length === 3);
		const findNodeChild = selectedKeys.filter(x => x.length === 5);
		const currentChild = node.key;
		//check if there is a child selected once open another parent group
		const lastChild = findNodeChild[findNodeChild.length - 1] ?? currentChild;
		const expandedKeys = [...findNodeParent, lastChild];
		let deviceObjectType: DeviceObjectType;
		let deviceType: DeviceType;
		treeData.forEach(x => {
			const deviceObj = x.children.find(w => w.key === lastChild);
			if (deviceObj) {
				deviceObjectType = deviceObj.deviceObjectType;
				deviceType = x.deviceType;
			}
		});

		this.setState({ expandedKeys, currentDeviceObject: deviceObjectType, currentDevice: deviceType });
		const currentDevice: CurrentDeviceControlObj = {
			Id: 0,
			DeviceObjectType: deviceObjectType,
			IsModalOpen: false,
		};

		onChangeDevice(currentDevice);
	};

	private handleSelectNode = (selectedKeys: React.Key[], { node }) => {
		const infoNode = node as EventDataNode;
		const currentNodeParentKey = selectedKeys[0].toString().substring(0, 3);
		if (selectedKeys[0].toString().length === 3) {
			return;
		}

		this.setState({ selectedKeys, propKey: new Date().getTime() });

		const currentDevice = treeData.find(x => x.key === currentNodeParentKey).children.find(x => x.key === infoNode.key).deviceObjectType;

		const {
			setCommandsSetsBy,
			setDoorGroupsBy,
			setStandardTimeZonesBy,
			setReaderControlGroupBy,
			setMasterTimeZonesBy,
			setGrandMasterTimeZonesBy,
			setContactsControllers,
			setCredentialTemplatesBy,
			setHolidaysBy,
			setOperatorsBy,
			setGlobalIOGroupsBy,
			setAllItemsPagination,
			setDefaultTablePaginationSetting,
			setDigitracFilterMode,
			currentTimeZone,
		} = this.props;

		setDefaultTablePaginationSetting();

		let objectType: ObjectTypes = ObjectTypes.none;
		switch (currentDevice) {
			case DeviceObjectType.CommandSets:
				setCommandsSetsBy();
				break;

			case DeviceObjectType.CredentialTemplates:
				setCredentialTemplatesBy();
				break;

			case DeviceObjectType.DoorGroup:
				setDoorGroupsBy();
				break;

			case DeviceObjectType.ReaderControlGroup:
				setReaderControlGroupBy();
				break;

			case DeviceObjectType.TimeZones:
				if (currentTimeZone === TimeZoneType.Grand) {
					setGrandMasterTimeZonesBy({ ...paginationSetting, SearchedValue: this.props.itemSearchedPagination });
					objectType = ObjectTypes.grandMasterTimeZones;
				} else if (currentTimeZone === TimeZoneType.Master) {
					setMasterTimeZonesBy({ ...paginationSetting, SearchedValue: this.props.itemSearchedPagination });
					objectType = ObjectTypes.masterTimeZones;
				} else {
					setStandardTimeZonesBy({ ...paginationSetting, SearchedValue: this.props.itemSearchedPagination });
					objectType = ObjectTypes.timeZones;
				}
				setAllItemsPagination(currentDevice, objectType);
				break;

			case DeviceObjectType.Holidays:
				setHolidaysBy();
				break;

			case DeviceObjectType.Operators:
				setOperatorsBy();
				break;

			case DeviceObjectType.GlobalIOGroup:
				setGlobalIOGroupsBy();
				break;

			default:
				setContactsControllers(currentDevice);
				setDigitracFilterMode(false);
		}
	};

	componentDidMount() {
		//set initial device data load
		this.props.setCommandsSetsBy();
	}

	componentDidCatch(error, errorInfo) {
		console.log(error, errorInfo);
	}

	componentDidUpdate(prevProps, prevState) {
		const { currentDevice, tablePaginationSetting, setContactsControllers } = this.props;

		if (prevProps.currentDevice !== currentDevice) {
			if (currentDevice.DeviceObjectType === DeviceObjectType.Controller) {
				this.setState({ selectedKeys: ['0-1-2'], currentDeviceObject: DeviceObjectType.Controller, currentDevice: DeviceType.DigiTrac });
				setContactsControllers(DeviceObjectType.Controller, tablePaginationSetting);
			}
		}
	}

	render() {
		const { expandedKeys, currentDeviceObject, currentDevice, propKey, selectedKeys } = this.state;
		const { DirectoryTree } = Tree;

		const renderSwitch = () => {
			switch (currentDevice) {
				case DeviceType.Velocity:
					return <VelocityConfiguration deviceObjectType={currentDeviceObject} propKey={propKey} />;

				case DeviceType.DigiTrac:
					return <DigiTrac deviceObjectType={currentDeviceObject} />;
			}
		};
		const isController = new URLSearchParams(location.search).get('controllerId');

		return (
			<>
				{!isController && (
					<div id='deviceControlContainer' className={styles.deviceHeaderBar}>
						<HeaderBar title={_('DeviceControl')} />
					</div>
				)}
				<div className={cx(styles.container, { [styles.grayContent]: !isController, [styles.whiteContent]: isController })}>
					{isController ? (
						<ControllerModal
							currentDevice={{
								DeviceObjectType: DeviceObjectType.Controller,
								Id: Number(isController),
							}}
							navigationParentTitle={_('DeviceControl')}
							navigationChildTitle={_('Controllers')}
						/>
					) : (
						<>
							<div>
								<DirectoryTree
									disabled={this.props.isEditMode}
									onExpand={!this.props.isEditMode ? this.handleExpand : () => null}
									treeData={treeData}
									expandedKeys={expandedKeys}
									selectedKeys={selectedKeys}
									onSelect={this.handleSelectNode}
									defaultSelectedKeys={selectedKeys}
									data-id={'deviceControlTree'}
								/>
							</div>
							<div>{renderSwitch()}</div>
						</>
					)}
				</div>
				{showEventsOnDeviceAdmPage && <EventTable />}
			</>
		);
	}
}

//TODO: FIX THIS
const mapStateToProps = (states: RootReducer, ownProps: OwnProps): StateProps => {
	return {
		currentDevice: states.commonReducer.currentDevice,
		isEditMode: states.deviceControlReducer.digiTrac.isEditMode || states.deviceControlReducer.velocityConfiguration.isEditMode,
		currentTimeZone: states.commonReducer.selectedTimeZone,
		itemSearchedPagination: states.commonReducer.itemSearchedPagination,
		tablePaginationSetting: states.deviceControlReducer.tablePaginationSetting,
	};
};

const mapDispatchToProps = (dispatch: ThunkDispatch<{}, {}, StoreEvent>, ownProps: OwnProps): DispatchProps => {
	return {
		onChangeDevice: async (deviceType: CurrentDeviceControlObj) => {
			await dispatch(setCurrentDevice(deviceType));
		},
		setCommandsSetsBy: async (paginationSetting?: PaginationSetting) => {
			await dispatch(setCommandsSetsBy(paginationSetting));
		},
		setContactsControllers: async (type: DeviceObjectType, paginationSetting?: PaginationSetting) => {
			await dispatch(setControllerContacts(type, paginationSetting));
		},
		setCredentialTemplatesBy: async (paginationSetting?: PaginationSetting) => {
			await dispatch(setCredentialTemplatesBy(paginationSetting));
		},
		setControllerContactsByData: async (type: DeviceObjectType, data: ControllerContact[]) => {
			await dispatch(setControllerContactsByData(type, data));
		},
		setDoorGroupsBy: async (paginationSetting?: PaginationSetting) => {
			await dispatch(setDoorGroupsBy(paginationSetting));
		},
		setGrandMasterTimeZonesBy: async (paginationSetting: PaginationSetting) => {
			await dispatch(setGrandTimeZonesBy(paginationSetting));
		},
		setHolidaysBy: async (paginationSetting?: PaginationSetting) => {
			await dispatch(setHolidaysBy(paginationSetting));
		},
		setOperatorsBy: async (paginationSetting?: PaginationSetting) => {
			await dispatch(setOperatorsBy(paginationSetting));
		},
		setMasterTimeZonesBy: async (paginationSetting: PaginationSetting) => {
			await dispatch(setMasterTimeZonesBy(paginationSetting));
		},
		setReaderControlGroupBy: async (paginationSetting?: PaginationSetting) => {
			await dispatch(setReaderControlGroupsBy(paginationSetting));
		},
		setStandardTimeZonesBy: async (paginationSetting: PaginationSetting) => {
			await dispatch(setStandardTimeZonesBy(paginationSetting));
		},
		setGlobalIOGroupsBy: async (paginationSetting?: PaginationSetting) => {
			await dispatch(setGlobalIOGroupsBy(paginationSetting));
		},
		setAllItemsPagination: async (deviceObjectType, objectType) => {
			await dispatch(setAllItemsPagination(deviceObjectType, objectType));
		},
		setDefaultTablePaginationSetting: async () => {
			await dispatch(setDefaultTablePaginationSetting());
		},
		setDigitracFilterMode: async (isFilterMode: boolean) => {
			await dispatch(setDigiTracFilterMode(isFilterMode));
		},
	};
};

const DeviceControlPage = connect<StateProps, DispatchProps, OwnProps>(mapStateToProps, mapDispatchToProps)(Page);

export { DeviceControlPage };
